import { useState, useEffect } from "react";
import { Clock, Trash2, Search } from "lucide-react";
import { BACKEND_URL } from "../../context/AuthContext";

export const PromptHistory = ({ authToken }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => { fetchHistory(); }, []);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/ai/history`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        setHistory(data.history || []);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const deletePrompt = async (promptId) => {
    try {
      await fetch(`${BACKEND_URL}/api/ai/history/${promptId}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${authToken}` },
      });
      setHistory(prev => prev.filter(h => h.prompt_id !== promptId));
    } catch (err) { console.error(err); }
  };

  const clearAll = async () => {
    if (!window.confirm('Clear all prompt history?')) return;
    try {
      await fetch(`${BACKEND_URL}/api/ai/history`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${authToken}` },
      });
      setHistory([]);
    } catch (err) { console.error(err); }
  };

  const filtered = history.filter(h =>
    h.prompt.toLowerCase().includes(search.toLowerCase()) ||
    h.provider.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div data-testid="ai-prompt-history">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Clock className="w-5 h-5 text-cyan-400" /> Prompt History
        </h3>
        {history.length > 0 && (
          <button onClick={clearAll} className="text-red-400/60 hover:text-red-400 text-xs transition" data-testid="ai-history-clear-all">
            Clear All
          </button>
        )}
      </div>

      {history.length > 3 && (
        <div className="relative mb-4">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search prompts..."
            className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-white text-sm placeholder-white/30 focus:outline-none focus:border-cyan-500/50"
            data-testid="ai-history-search" />
        </div>
      )}

      {loading ? (
        <div className="text-center py-8"><div className="w-6 h-6 border-2 border-white/20 border-t-cyan-500 rounded-full animate-spin mx-auto" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 bg-white/[0.02] border border-white/[0.06] rounded-2xl">
          <Clock className="w-10 h-10 text-white/10 mx-auto mb-3" />
          <p className="text-white/30 text-sm">{search ? 'No matching prompts' : 'No prompt history yet'}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(item => (
            <div key={item.prompt_id} className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-4" data-testid={`ai-history-item-${item.prompt_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm truncate">{item.prompt}</p>
                  {item.response && <p className="text-white/40 text-xs truncate mt-1">{item.response}</p>}
                  <div className="flex items-center gap-3 mt-2 text-[11px] text-white/30">
                    <span className="text-cyan-400/70">{item.provider}</span>
                    <span>{item.model}</span>
                    {item.metadata?.is_mock && <span className="text-amber-400/50">mock</span>}
                    <span>{new Date(item.created_at).toLocaleString()}</span>
                  </div>
                </div>
                <button onClick={() => deletePrompt(item.prompt_id)}
                  className="text-white/20 hover:text-red-400 transition flex-shrink-0 p-1">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
